import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Card, CardContent, Chip, CircularProgress, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SubmissionUploadDialog from '../shared/SubmissionUploadDialog';
import LearnerImpersonationDialog from '../shared/LearnerImpersonationDialog';

interface SchoolClass {
  id: number;
  name: string;
  subject: string;
  description: string;
  teacher: {
    id: number;
    first_name: string;
    last_name: string;
  };
  grade_level: number | null;
  adam_class_id: number | null;
}

interface Submission {
  id: number;
  assessment_name: string;
  class_name: string;
  submitted_at: string;
  grade: number | null;
  status: string;
  assignment_id?: number;
}

interface Assessment {
  id: number;
  title: string;
  description?: string | null;
  due_date: string;
  max_points: number;
  class_id?: number;
  is_active: boolean;
  submission_format?: 'file' | 'text' | 'both';
  allowed_file_types?: string[] | null;
  assigned_classes_display?: Array<{
    id: number;
    name: string;
    grade_level: number;
    subject: string;
  }>;
}

const Dashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { session } = useImpersonation();
  const navigate = useNavigate();
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [impersonationDialogOpen, setImpersonationDialogOpen] = useState(false);

  // When impersonating a learner, treat the role as 'student'
  const userRole = session.is_impersonating ? 'student' : user?.user_profile?.role;

  const handleOpenUploadDialog = (assessment: Assessment) => {
    // Double-check that no submission exists for this assessment
    const existingSubmission = submissions.find(s => s.assignment_id === assessment.id);
    if (existingSubmission) {
      alert('You have already submitted this assessment. Only one submission is allowed.');
      return;
    }
    setSelectedAssessment(assessment);
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedAssessment(null);
  };

  const handleUploadSuccess = () => {
    // Refresh submissions and assessments
    if (userRole === 'student') {
      fetchLearnerData();
    }
  };

  useEffect(() => {
    if (userRole === 'student') {
      fetchLearnerData();
    } else if (userRole === 'teacher') {
      fetchTeacherData();
    } else {
      setLoading(false);
    }
  }, [userRole, session.is_impersonating]); // Re-fetch when impersonation changes

  const fetchLearnerData = async () => {
    try {
      // Fetch all data in parallel
      const [classesRes, submissionsRes, assessmentsRes] = await Promise.all([
        axios.get('/api/learners/me/classes/'),
        axios.get('/api/learners/me/submissions/'),
        axios.get('/api/assignments/')
      ]);

      setClasses(classesRes.data.classes || []);
      setSubmissions((submissionsRes.data.submissions || []).slice(0, 5)); // Latest 5

      // Filter and sort assessments by due date
      const now = new Date();
      const upcoming = assessmentsRes.data.results
        .filter((assessment: Assessment) => {
          if (!assessment.is_active) return false;
          const dueDate = new Date(assessment.due_date);
          // Show assessments due in the next 30 days or overdue by up to 7 days
          const daysDiff = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
          return daysDiff > -7 && daysDiff < 30;
        })
        .sort((a: Assessment, b: Assessment) =>
          new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
        )
        .slice(0, 8); // Show up to 8 upcoming assessments

      setAssessments(upcoming);
    } catch (error) {
      console.error('Error fetching learner data:', error);
      setClasses([]); // Set empty array on error
      setSubmissions([]); // Set empty array on error
      setAssessments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherData = async () => {
    try {
      // Fetch teacher's classes
      const classesRes = await axios.get('/api/teachers/me/classes/');
      setClasses(classesRes.data.classes || []);
    } catch (error) {
      console.error('Error fetching teacher data:', error);
      setClasses([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get urgency status for an assessment
  const getAssessmentUrgency = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const hoursUntilDue = (due.getTime() - now.getTime()) / (1000 * 60 * 60);

    if (hoursUntilDue < 0) {
      return { label: 'Overdue', color: 'error' as const, icon: <WarningIcon fontSize="small" /> };
    } else if (hoursUntilDue < 24) {
      return { label: 'Due Today', color: 'error' as const, icon: <WarningIcon fontSize="small" /> };
    } else if (hoursUntilDue < 48) {
      return { label: 'Due Tomorrow', color: 'warning' as const, icon: <EventIcon fontSize="small" /> };
    } else if (hoursUntilDue < 168) { // 7 days
      return { label: 'Due This Week', color: 'warning' as const, icon: <EventIcon fontSize="small" /> };
    } else {
      return { label: 'Upcoming', color: 'info' as const, icon: <EventIcon fontSize="small" /> };
    }
  };

  // Helper function to format due date
  const formatDueDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    if (diffMs < 0) {
      const overdueDays = Math.abs(diffDays);
      return `${formattedDate} (${overdueDays} day${overdueDays !== 1 ? 's' : ''} overdue)`;
    } else if (diffHours < 24) {
      return `${formattedDate} (in ${diffHours}h)`;
    } else if (diffDays === 0) {
      return `${formattedDate} (today)`;
    } else if (diffDays === 1) {
      return `${formattedDate} (tomorrow)`;
    } else if (diffDays < 7) {
      return `${formattedDate} (in ${diffDays} days)`;
    } else {
      return formattedDate;
    }
  };

  // Check if student has submitted to an assessment
  const hasSubmitted = (assessmentId: number) => {
    return submissions.some(sub => sub.assignment_id === assessmentId);
  };

  // Learner Dashboard
  if (userRole === 'student') {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ pt: 2, pr: 2, pb: 2 }}>
        <Typography variant="h4" gutterBottom>
          My Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Welcome back, {session.is_impersonating && session.learner ? session.learner.name : (user?.first_name || user?.username)}!
        </Typography>

        {/* My Classes */}
        <Box mt={4}>
          <Typography variant="h5" gutterBottom display="flex" alignItems="center">
            <SchoolIcon sx={{ mr: 1 }} /> My Classes
          </Typography>
          {!Array.isArray(classes) || classes.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No classes found. Classes will appear here after logging in with ADAM authentication.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2, mt: 1 }}>
              {classes.map((cls) => (
                <Card
                  key={cls.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 }
                  }}
                  onClick={() => navigate(`/classes/${cls.id}/content`)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {cls.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {cls.subject}
                    </Typography>
                    <Box mt={2}>
                      <Chip
                        label={`Grade ${cls.grade_level || 'N/A'}`}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {cls.adam_class_id && (
                        <Chip
                          label="ADAM Synced"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="caption" display="block" mt={2}>
                      Teacher: {cls.teacher.first_name} {cls.teacher.last_name}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {/* Upcoming Assessments */}
        <Box mt={4}>
          <Typography variant="h5" gutterBottom display="flex" alignItems="center">
            <AssignmentIcon sx={{ mr: 1 }} /> Upcoming Assessments
          </Typography>
          {assessments.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No upcoming assessments in the next 30 days.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2, mt: 1 }}>
              {assessments.map((assessment) => {
                const urgency = getAssessmentUrgency(assessment.due_date);
                const submitted = hasSubmitted(assessment.id);
                const className = assessment.assigned_classes_display?.[0]?.name || 'N/A';

                return (
                  <Card
                    key={assessment.id}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderLeft: 4,
                      borderColor: urgency.color === 'error' ? 'error.main' :
                                  urgency.color === 'warning' ? 'warning.main' : 'info.main'
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1, mr: 1 }}>
                          {assessment.title}
                        </Typography>
                        {submitted && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                      </Box>

                      {assessment.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {assessment.description}
                        </Typography>
                      )}

                      <Box mb={1}>
                        <Chip
                          label={urgency.label}
                          color={urgency.color}
                          size="small"
                          icon={urgency.icon}
                          sx={{ mr: 1 }}
                        />
                        {submitted && (
                          <Chip
                            label="Submitted"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      <Typography variant="caption" display="block" color="text.secondary">
                        Class: {className}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Due: {formatDueDate(assessment.due_date)}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Points: {assessment.max_points}
                      </Typography>

                      {!submitted && (
                        <Box mt={2}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenUploadDialog(assessment);
                            }}
                            fullWidth
                          >
                            Submit Work
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Recent Submissions */}
        <Box mt={4}>
          <Typography variant="h5" gutterBottom display="flex" alignItems="center">
            <AssignmentIcon sx={{ mr: 1 }} /> Recent Submissions
          </Typography>
          {submissions.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No submissions yet. Your submitted work will appear here.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2, mt: 1 }}>
              {submissions.map((submission) => {
                // Determine status chip properties
                const getStatusChip = () => {
                  if (submission.grade !== null && submission.grade !== undefined) {
                    return {
                      label: `${submission.grade}%`,
                      color: 'success' as const,
                      icon: <CheckCircleIcon fontSize="small" />
                    };
                  } else if (submission.status === 'marked') {
                    return {
                      label: 'Marked',
                      color: 'success' as const,
                      icon: <CheckCircleIcon fontSize="small" />
                    };
                  } else if (submission.status === 'pending') {
                    return {
                      label: 'Awaiting Review',
                      color: 'warning' as const,
                      icon: <EventIcon fontSize="small" />
                    };
                  } else {
                    return {
                      label: 'Submitted',
                      color: 'info' as const,
                      icon: <EventIcon fontSize="small" />
                    };
                  }
                };

                const statusChip = getStatusChip();
                const submittedDate = new Date(submission.submitted_at);
                const formattedDate = submittedDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });

                return (
                  <Card
                    key={submission.id}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 2 }
                    }}
                    onClick={() => {
                      navigate(`/submissions/${submission.id}/mark`);
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom noWrap>
                        {submission.assessment_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {submission.class_name}
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
                        <Chip
                          label={statusChip.label}
                          color={statusChip.color}
                          size="small"
                          icon={statusChip.icon}
                        />
                      </Box>

                      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                        Submitted: {formattedDate}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Submission Upload Dialog */}
        {selectedAssessment && (
          <SubmissionUploadDialog
            open={uploadDialogOpen}
            onClose={handleCloseUploadDialog}
            assessmentId={selectedAssessment.id}
            assessmentTitle={selectedAssessment.title}
            submissionFormat={selectedAssessment.submission_format || 'file'}
            allowedFileTypes={selectedAssessment.allowed_file_types}
            onSuccess={handleUploadSuccess}
          />
        )}
      </Box>
    );
  }

  // Teacher Dashboard
  if (userRole === 'teacher') {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ pt: 2, pr: 2, pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Teacher Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Welcome back, {user?.first_name || user?.username}!
            </Typography>
          </Box>
          {!session.is_impersonating && (
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              onClick={() => setImpersonationDialogOpen(true)}
            >
              View as Learner
            </Button>
          )}
        </Box>

        <Box mt={4}>
          <Typography variant="h5" gutterBottom display="flex" alignItems="center">
            <PeopleIcon sx={{ mr: 1 }} /> My Classes
          </Typography>
          {Array.isArray(classes) && classes.length > 0 ? (
            (() => {
              // Group classes by subject
              const groupedClasses = classes.reduce((acc, cls) => {
                const subject = cls.subject || 'Other';
                if (!acc[subject]) {
                  acc[subject] = [];
                }
                acc[subject].push(cls);
                return acc;
              }, {} as Record<string, SchoolClass[]>);

              // Sort classes within each subject by grade level
              Object.keys(groupedClasses).forEach(subject => {
                groupedClasses[subject].sort((a, b) => {
                  if (a.grade_level && b.grade_level) {
                    return a.grade_level - b.grade_level;
                  }
                  return a.name.localeCompare(b.name);
                });
              });

              // Sort subjects alphabetically
              const sortedSubjects = Object.keys(groupedClasses).sort();

              return (
                <Box sx={{ mt: 1 }}>
                  {sortedSubjects.map((subject, index) => (
                    <Accordion key={subject} defaultExpanded={index === 0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          {subject} ({groupedClasses[subject].length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                          {groupedClasses[subject].map((cls) => (
                            <Card key={cls.id} onClick={() => navigate(`/classes/${cls.id}`)} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
                              <CardContent>
                                <Typography variant="h6" noWrap>{cls.name}</Typography>
                                {cls.grade_level && (
                                  <Chip label={`Grade ${cls.grade_level}`} size="small" sx={{ mt: 1 }} />
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              );
            })()
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                No classes found. You may not have any classes assigned yet.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Impersonation Dialog */}
        <LearnerImpersonationDialog
          open={impersonationDialogOpen}
          onClose={() => setImpersonationDialogOpen(false)}
        />
      </Box>
    );
  }

  // Still resolving auth — show spinner rather than the admin fallback
  if (authLoading || !userRole) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Admin Dashboard (default for superuser/admin roles)
  return (
    <Box sx={{ pt: 2, pr: 2, pb: 2 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Schools</Typography>
          <Typography variant="h3">12</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Teachers</Typography>
          <Typography variant="h3">145</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Learners</Typography>
          <Typography variant="h3">1,234</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Assessments</Typography>
          <Typography variant="h3">89</Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
